/**
 * 📱 MOBILE-OPTIMIZED CACHE SERVICE
 * Aggressive caching strategy for mobile performance
 * Implements intelligent cache management with bandwidth awareness
 */

import { isSlowConnection, isMobileDevice } from '@/utils/imageOptimization';

interface MobileCacheEntry<T = any> {
  data: T;
  timestamp: number;
  expiration: number;
  priority: 'critical' | 'high' | 'medium' | 'low';
  size: number; // Estimated size in bytes
  accessCount: number;
  lastAccessed: number;
}

interface CacheStats {
  totalSize: number;
  entryCount: number;
  hitRate: number;
  totalHits: number;
  totalMisses: number;
}

class MobileCacheService {
  private cache: Map<string, MobileCacheEntry> = new Map();
  private maxSize: number;
  private stats: CacheStats = {
    totalSize: 0,
    entryCount: 0,
    hitRate: 0,
    totalHits: 0,
    totalMisses: 0
  };

  constructor() {
    // Adaptive cache size based on device capabilities
    this.maxSize = this.calculateOptimalCacheSize();
    this.startCleanupInterval();
  }

  /**
   * Calculate optimal cache size based on device and connection
   */
  private calculateOptimalCacheSize(): number {
    const isMobile = isMobileDevice();
    const isSlowConn = isSlowConnection();

    if (isSlowConn) {
      return 5 * 1024 * 1024; // 5MB for slow connections
    } else if (isMobile) {
      return 20 * 1024 * 1024; // 20MB for mobile
    } else {
      return 50 * 1024 * 1024; // 50MB for desktop
    }
  }

  /**
   * Estimate data size in bytes
   */
  private estimateSize(data: any): number {
    try {
      return new Blob([JSON.stringify(data)]).size;
    } catch {
      return JSON.stringify(data).length * 2; // Rough estimate
    }
  }

  /**
   * Get cache entry with LRU tracking
   */
  get<T>(key: string): T | null {
    const entry = this.cache.get(key);
    
    if (!entry) {
      this.stats.totalMisses++;
      this.updateHitRate();
      return null;
    }

    // Check expiration
    if (Date.now() > entry.expiration) {
      this.cache.delete(key);
      this.stats.totalMisses++;
      this.updateHitRate();
      return null;
    }

    // Update access tracking
    entry.accessCount++;
    entry.lastAccessed = Date.now();
    
    this.stats.totalHits++;
    this.updateHitRate();
    
    return entry.data as T;
  }

  /**
   * Set cache entry with intelligent eviction
   */
  set<T>(
    key: string, 
    data: T, 
    options: {
      ttl?: number;
      priority?: 'critical' | 'high' | 'medium' | 'low';
    } = {}
  ): boolean {
    const size = this.estimateSize(data);
    const ttl = options.ttl || this.getDefaultTTL(options.priority || 'medium');
    
    // Check if we need to make space
    if (this.stats.totalSize + size > this.maxSize) {
      this.evictLeastImportant(size);
    }

    const entry: MobileCacheEntry<T> = {
      data,
      timestamp: Date.now(),
      expiration: Date.now() + ttl,
      priority: options.priority || 'medium',
      size,
      accessCount: 0,
      lastAccessed: Date.now()
    };

    this.cache.set(key, entry);
    this.stats.totalSize += size;
    this.stats.entryCount++;

    return true;
  }

  /**
   * Get default TTL based on priority and connection
   */
  private getDefaultTTL(priority: string): number {
    const isSlowConn = isSlowConnection();
    const multiplier = isSlowConn ? 2 : 1; // Cache longer on slow connections

    const baseTTL = {
      critical: 60 * 60 * 1000, // 1 hour
      high: 30 * 60 * 1000,     // 30 minutes
      medium: 15 * 60 * 1000,   // 15 minutes
      low: 5 * 60 * 1000        // 5 minutes
    };

    return baseTTL[priority as keyof typeof baseTTL] * multiplier;
  }

  /**
   * Evict least important entries to make space
   */
  private evictLeastImportant(requiredSpace: number): void {
    const entries = Array.from(this.cache.entries());
    
    // Sort by importance (priority + access frequency + recency)
    entries.sort(([, a], [, b]) => {
      const priorityWeight = { critical: 4, high: 3, medium: 2, low: 1 };
      const aScore = priorityWeight[a.priority] * a.accessCount * (1 / (Date.now() - a.lastAccessed));
      const bScore = priorityWeight[b.priority] * b.accessCount * (1 / (Date.now() - b.lastAccessed));
      return aScore - bScore; // Ascending order (least important first)
    });

    let freedSpace = 0;
    for (const [key, entry] of entries) {
      if (freedSpace >= requiredSpace) break;
      
      this.cache.delete(key);
      this.stats.totalSize -= entry.size;
      this.stats.entryCount--;
      freedSpace += entry.size;
    }
  }

  /**
   * Update hit rate statistics
   */
  private updateHitRate(): void {
    const total = this.stats.totalHits + this.stats.totalMisses;
    this.stats.hitRate = total > 0 ? this.stats.totalHits / total : 0;
  }

  /**
   * Preload critical data for mobile
   */
  async preloadCritical(keys: string[], dataLoader: (key: string) => Promise<any>): Promise<void> {
    const promises = keys.map(async (key) => {
      if (!this.get(key)) {
        try {
          const data = await dataLoader(key);
          this.set(key, data, { priority: 'critical' });
        } catch (error) {
          console.warn(`Failed to preload ${key}:`, error);
        }
      }
    });

    await Promise.allSettled(promises);
  }

  /**
   * Clear expired entries
   */
  private cleanup(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now > entry.expiration) {
        this.cache.delete(key);
        this.stats.totalSize -= entry.size;
        this.stats.entryCount--;
      }
    }
  }

  /**
   * Start periodic cleanup
   */
  private startCleanupInterval(): void {
    setInterval(() => {
      this.cleanup();
    }, 5 * 60 * 1000); // Cleanup every 5 minutes
  }

  /**
   * Get cache statistics
   */
  getStats(): CacheStats {
    return { ...this.stats };
  }

  /**
   * Clear all cache
   */
  clear(): void {
    this.cache.clear();
    this.stats = {
      totalSize: 0,
      entryCount: 0,
      hitRate: 0,
      totalHits: 0,
      totalMisses: 0
    };
  }

  /**
   * Remove specific entry
   */
  remove(key: string): boolean {
    const entry = this.cache.get(key);
    if (entry) {
      this.cache.delete(key);
      this.stats.totalSize -= entry.size;
      this.stats.entryCount--;
      return true;
    }
    return false;
  }

  /**
   * Check if key exists and is not expired
   */
  has(key: string): boolean {
    const entry = this.cache.get(key);
    if (!entry) return false;
    
    if (Date.now() > entry.expiration) {
      this.cache.delete(key);
      return false;
    }
    
    return true;
  }
}

// Global mobile cache instance
export const mobileCache = new MobileCacheService();

// Convenience functions for common cache patterns
export const mobileCacheHelpers = {
  // Cache user profiles with high priority
  cacheUserProfile: (userId: string, profile: any) => 
    mobileCache.set(`profile_${userId}`, profile, { priority: 'high', ttl: 30 * 60 * 1000 }),

  // Cache user avatars with critical priority
  cacheUserAvatar: (userId: string, avatarUrl: string) =>
    mobileCache.set(`avatar_${userId}`, avatarUrl, { priority: 'critical', ttl: 60 * 60 * 1000 }),

  // Cache conversations with medium priority
  cacheConversations: (userId: string, conversations: any[]) =>
    mobileCache.set(`conversations_${userId}`, conversations, { priority: 'medium', ttl: 15 * 60 * 1000 }),

  // Cache discover matches with low priority
  cacheDiscoverMatches: (userId: string, matches: any[]) =>
    mobileCache.set(`discover_${userId}`, matches, { priority: 'low', ttl: 10 * 60 * 1000 }),

  // Get cached data with fallback
  getCachedOrFetch: async <T>(key: string, fetcher: () => Promise<T>): Promise<T> => {
    const cached = mobileCache.get<T>(key);
    if (cached) return cached;

    const data = await fetcher();
    mobileCache.set(key, data);
    return data;
  }
};

export default mobileCache;

